import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../utils/helpers';

interface BaseProps {
  icon?: LucideIcon;
  label: string;
  description?: string;
  /** Right-aligned content (toggle, value, badge). */
  trailing?: ReactNode;
  danger?: boolean;
  className?: string;
}

type RowProps =
  | (BaseProps & { to: string; onClick?: never })
  | (BaseProps & { onClick: () => void; to?: never })
  | (BaseProps & { to?: undefined; onClick?: undefined });

/**
 * One settings row. Renders as a link (`to`), a button (`onClick`), or a static
 * row. A chevron is shown automatically for navigable rows.
 */
export default function SettingsRow({ icon: Icon, label, description, trailing, danger, className, ...rest }: RowProps) {
  const navigable = 'to' in rest || 'onClick' in rest;

  const inner = (
    <>
      <div className="flex items-center gap-3.5 min-w-0">
        {Icon && (
          <div
            className={cn(
              'w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 transition-colors',
              danger
                ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-500'
                : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 group-hover:bg-neutral-900 group-hover:text-white dark:group-hover:bg-neutral-100 dark:group-hover:text-neutral-900',
            )}
          >
            <Icon className="w-5 h-5" />
          </div>
        )}
        <div className="min-w-0 text-left">
          <p className={cn('font-semibold text-sm truncate', danger ? 'text-rose-600 dark:text-rose-400' : 'text-neutral-900 dark:text-neutral-50')}>{label}</p>
          {description && <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{description}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {trailing}
        {navigable && <ChevronRight className={cn('w-5 h-5', danger ? 'text-rose-300 dark:text-rose-800' : 'text-neutral-300 dark:text-neutral-700 group-hover:text-neutral-900 dark:group-hover:text-neutral-100')} />}
      </div>
    </>
  );

  const base = cn(
    'group w-full flex items-center justify-between gap-4 px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 last:border-0 transition-colors',
    navigable && (danger ? 'hover:bg-rose-50 dark:hover:bg-rose-950/30' : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/60'),
    className,
  );

  if ('to' in rest && rest.to) {
    return (
      <Link to={rest.to} className={base}>
        {inner}
      </Link>
    );
  }
  if ('onClick' in rest && rest.onClick) {
    return (
      <button type="button" onClick={rest.onClick} className={base}>
        {inner}
      </button>
    );
  }
  return <div className={base}>{inner}</div>;
}
